/**
 * Lift2Fit — coaching workspace: the client's logged progress, read-only.
 *
 * The coach's answer to "how is their training actually going": every completed
 * session with its date, label, sets, volume, RPE and PRs, plus the adherence
 * picture against the block's prescription (the repeating week × the weeks) and
 * per week. This is a coaching tool, not a chart show — dense rows, no graphs,
 * and no way to edit a log from here.
 *
 * The API is the gate: a client's sessions are only served when the link shares
 * `sessions`, and set-derived numbers only when it also shares `sets`. This panel
 * renders the withheld fields as "not shared" rather than as a zero.
 */
import { useMemo } from 'react';
import { api, type ClientStatsSummary, type ProgrammePlan, type SessionTotalsWire } from '@/lib/api';
import { formatCompactCount, formatCompactVolume, formatCount } from '@/lib/format';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { formatVolume } from '@/lib/units';
import { AlertIcon, CheckIcon, SparkIcon, TrendUpIcon } from '@/lib/icons';
import {
  AdherenceBar,
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  StatusChip,
} from '../components';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { formatDateShort } from './shared';

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` of an ISO instant, in UTC — the API's own day. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

function wholeDaysBetween(fromDay: string, toDay: string): number {
  return Math.round((Date.parse(toDay) - Date.parse(fromDay)) / DAY_MS);
}

function volumeLabel(kg: number, unit: 'metric' | 'imperial'): string {
  if (unit === 'imperial') return `${Math.round(kg * 2.20462).toLocaleString('en-GB')} lb`;
  return `${Math.round(kg).toLocaleString('en-GB')} kg`;
}

/**
 * A session's own compliance from the totals the list already carries: sets
 * done against sets prescribed. The per-set detail (rep range, load, rest) lives
 * in the session's own table, one Open away.
 */
function sessionCompliance(session: SessionTotalsWire): {
  label: string;
  word: 'Met' | 'Short' | 'Added' | 'No target';
  tone: 'success' | 'warning' | 'neutral';
  short: boolean;
} {
  const prescribed = session.setsPrescribed;
  if (prescribed === null || prescribed === 0) {
    return { label: `${session.setsCompleted} sets`, word: 'No target', tone: 'neutral', short: false };
  }
  const label = `${session.setsCompleted} / ${prescribed}`;
  if (session.setsCompleted < prescribed) return { label, word: 'Short', tone: 'warning', short: true };
  if (session.setsCompleted > prescribed) return { label, word: 'Added', tone: 'neutral', short: false };
  return { label, word: 'Met', tone: 'success', short: false };
}

export function ClientSessionsPanel({
  clientId,
  clientName,
  sharedFields,
  plan,
}: {
  clientId: string;
  clientName: string;
  sharedFields: ReadonlyArray<'sessions' | 'sets' | 'bodyweight' | 'notes' | 'pain'>;
  plan: ProgrammePlan | null;
}) {
  const { unit } = useUnitPreference();
  const sharesSessions = sharedFields.includes('sessions');
  const sharesSets = sharedFields.includes('sets');
  const sessionsState = useAsync(
    () => (sharesSessions ? api.clientSessions(clientId, 60) : Promise.resolve({ data: [], source: 'api' as const })),
    [clientId, sharesSessions],
  );
  const statsState = useAsync(
    () => (sharesSessions && sharesSets ? api.clientStats(clientId) : Promise.resolve({ data: null, source: 'api' as const })),
    [clientId, sharesSessions, sharesSets],
  );

  const sessions = sessionsState.data ?? [];
  const stats: ClientStatsSummary | null = statsState.data ?? null;

  const completed = useMemo(
    () => sessions.filter((session) => session.status === 'completed'),
    [sessions],
  );

  const block = plan?.block ?? null;
  const weeks = block?.weeks ?? plan?.weeks ?? null;
  const daysPerWeek = plan?.daysPerWeek ?? null;
  const prescribed = weeks !== null && daysPerWeek !== null ? weeks * daysPerWeek : null;

  const inBlock = useMemo(() => {
    if (!block) return completed;
    return completed.filter((session) => {
      const day = dayOf(session.startedAt);
      return day >= block.startsOn && day <= block.endsOn;
    });
  }, [completed, block]);

  const perWeek = useMemo(() => {
    if (!block || weeks === null) return [];
    const counts = new Array<number>(weeks).fill(0);
    for (const session of completed) {
      const index = Math.floor(wholeDaysBetween(block.startsOn, dayOf(session.startedAt)) / 7);
      if (index >= 0 && index < weeks) counts[index] += 1;
    }
    return counts.map((count, index) => ({ week: index + 1, count, isDeload: plan?.deloadWeek === index + 1 }));
  }, [completed, block, weeks, plan?.deloadWeek]);

  const totalVolume = completed.reduce((sum, session) => sum + session.volumeKg, 0);
  const totalPrs = completed.reduce((sum, session) => sum + session.prCount, 0);
  const adherencePct =
    prescribed === null || prescribed === 0
      ? null
      : Math.min(100, Math.round((inBlock.length / prescribed) * 100));

  return (
    <Panel
      testId="crm-client-history"
      title="Sessions (read-only)"
      subtitle={`${clientName}'s completed sessions. You can read them and cannot edit them — their log stays theirs.`}
      actions={
        stats && stats.currentStreakWeeks > 0 ? (
          <StatusChip tone="success" icon={<TrendUpIcon />}>
            {stats.currentStreakWeeks} week streak
          </StatusChip>
        ) : null
      }
      bodyClassName="gap-4"
    >
      {!sharesSessions ? (
        <EmptyState
          title="Sessions are not shared"
          body="This client has not shared their session history with you. They can turn it on from their Manage coach page; the API withholds every session row until then."
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
        />
      ) : sessionsState.loading && !sessionsState.data ? (
        <LoadingPanel label="Loading their sessions" rows={6} />
      ) : sessionsState.error ? (
        <p className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger">{sessionsState.error}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <MetricTile
              label="Completed"
              value={formatCompactCount(completed.length)}
              valueTitle={formatCount(completed.length)}
              hint={block ? `${inBlock.length} inside the current block` : 'all time'}
            />
            <MetricTile
              label="Block adherence"
              value={adherencePct === null ? '—' : `${adherencePct}%`}
              hint={prescribed === null ? 'no active block' : `${inBlock.length} of ${prescribed} prescribed`}
              hintTone={adherencePct !== null && adherencePct >= 80 ? 'success' : adherencePct !== null && adherencePct >= 60 ? 'warning' : 'danger'}
            />
            <MetricTile
              label="Volume"
              value={sharesSets ? formatCompactVolume(totalVolume, unit) : 'Not shared'}
              valueTitle={sharesSets ? formatVolume(totalVolume, unit) : undefined}
              hint={sharesSets ? 'logged sets, all shown sessions' : 'sets are not shared'}
            />
            <MetricTile
              label="Avg RPE"
              value={stats?.avgRpe === null || stats === null ? '—' : String(stats.avgRpe)}
              hint={sharesSets ? 'last 28 days' : 'sets are not shared'}
            />
            <MetricTile
              label="PRs"
              value={sharesSets ? formatCompactCount(totalPrs) : 'Not shared'}
              valueTitle={sharesSets ? formatCount(totalPrs) : undefined}
              hint={sharesSets ? 'across the sessions below' : 'sets are not shared'}
            />
          </dl>

          {block ? (
            <section aria-label="Adherence inside the block" data-testid="client-history-adherence" className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <AdherenceBar pct={adherencePct} />
                <span className="text-[12px] text-ink-muted">
                  Week {block.weekOf} of {block.weeks} · block ends {formatDateShort(block.endsOn)}
                </span>
              </div>
              {perWeek.length ? (
                <ul className="l2f-strip-scroll flex gap-2 pb-1" aria-label="Sessions completed per week">
                  {perWeek.map((entry) => (
                    <li
                      key={entry.week}
                      data-testid={`client-history-week-${entry.week}`}
                      className="flex min-w-[92px] shrink-0 flex-col gap-0.5 border border-hairline bg-page p-2"
                    >
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                        Week {entry.week}
                        {entry.isDeload ? ' · deload' : ''}
                      </span>
                      <span className="l2f-tabular font-mono text-[14px] font-bold text-ink">
                        {entry.count}
                        {daysPerWeek === null ? '' : ` / ${daysPerWeek}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          {/* Block compliance, extended with the session-level detail the
              weekly strip cannot show: which session fell short, and a way in. */}
          {block && sharesSets && inBlock.length ? (
            <section
              aria-label="Block compliance, session by session"
              data-testid="client-block-compliance"
              className="flex flex-col gap-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  Block compliance — session by session
                </h3>
                <span className="text-[12px] text-ink-muted">
                  {inBlock.filter((session) => sessionCompliance(session).short).length} of {inBlock.length} short of the
                  prescribed sets
                </span>
              </div>
              <ul className="flex flex-col divide-y divide-hairline">
                {inBlock.map((session) => {
                  const state = sessionCompliance(session);
                  const weekIndex = Math.floor(wholeDaysBetween(block.startsOn, dayOf(session.startedAt)) / 7) + 1;
                  const href = `/crm/clients/${encodeURIComponent(clientId)}/sessions/${encodeURIComponent(session.sessionId)}`;
                  return (
                    <li
                      key={session.sessionId}
                      data-testid={`client-block-compliance-${session.sessionId}`}
                      className="flex flex-wrap items-center justify-between gap-2 py-2"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="text-[13px] font-bold text-ink">{session.label ?? 'Session'}</span>
                        <span className="text-[11px] text-ink-muted">
                          {formatDateShort(session.startedAt)}
                          {weekIndex >= 1 && weekIndex <= block.weeks ? ` · week ${weekIndex}` : ''}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="l2f-tabular font-mono text-[13px] text-ink">{state.label} sets</span>
                        <StatusChip tone={state.tone}>{state.word}</StatusChip>
                        <Link
                          to={href}
                          data-testid={`client-block-compliance-open-${session.sessionId}`}
                          className="inline-flex h-8 items-center border border-hairline px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                        >
                          Open
                        </Link>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {completed.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left" data-testid="client-history-table">
                <thead>
                  <tr className="border-b border-hairline">
                    {['Date', 'Session', 'Sets', ...(sharesSets ? ['Compliance'] : []), 'Volume', 'RPE', 'PRs', ''].map(
                      (heading) => (
                        <th
                          key={heading || 'open'}
                          scope="col"
                          className="px-2 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                        >
                          {heading}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {completed.map((session) => {
                    const state = sessionCompliance(session);
                    const href = `/crm/clients/${encodeURIComponent(clientId)}/sessions/${encodeURIComponent(session.sessionId)}`;
                    return (
                      <tr
                        key={session.sessionId}
                        data-testid={`client-history-row-${session.sessionId}`}
                        className="border-b border-hairline last:border-b-0"
                      >
                        <td className="whitespace-nowrap px-2 py-2 text-[13px] text-ink">
                          {formatDateShort(session.startedAt)}
                        </td>
                        <td className="px-2 py-2 text-[13px] font-bold text-ink">
                          {session.label ?? 'Session'}
                          {session.failedSets > 0 ? (
                            <span className="ml-2 text-[11px] font-normal text-warning">{session.failedSets} failed</span>
                          ) : null}
                        </td>
                        <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">
                          {sharesSets ? state.label : '—'}
                        </td>
                        {sharesSets ? (
                          <td className="px-2 py-2">
                            <StatusChip tone={state.tone}>{state.word}</StatusChip>
                          </td>
                        ) : null}
                        <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">
                          {sharesSets ? volumeLabel(session.volumeKg, unit) : '—'}
                        </td>
                        <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">
                          {sharesSets && session.avgRpe !== null ? String(session.avgRpe) : '—'}
                        </td>
                        <td className="px-2 py-2">
                          {sharesSets && session.prCount > 0 ? (
                            <StatusChip tone="accent" icon={<CheckIcon />}>
                              {session.prCount} PR
                            </StatusChip>
                          ) : (
                            <span className="text-[13px] text-ink-muted">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {sharesSets ? (
                            <Link
                              to={href}
                              data-testid={`client-history-open-${session.sessionId}`}
                              className="inline-flex h-8 items-center border border-hairline px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                            >
                              Open
                            </Link>
                          ) : (
                            <span className="text-[13px] text-ink-muted">Not shared</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title="No completed sessions yet"
              body={`${clientName} has not completed a session. Once they train, every completed session lands here with its sets, volume, RPE and PRs.`}
              icon={<SparkIcon className="h-8 w-8 text-accent" />}
            />
          )}
        </>
      )}
    </Panel>
  );
}
